import { useRef, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Bug, Lightbulb, MessageSquare, Star, Upload, X } from "lucide-react";

type FeedbackType = "bug" | "feature" | "general";

interface FeedbackFormProps {
  onSubmitted?: () => void;
  onCancel?: () => void;
  submitLabel?: string;
  showCancel?: boolean;
  className?: string;
}

export function FeedbackForm({
  onSubmitted,
  onCancel,
  submitLabel = "Submit Feedback",
  showCancel = false,
  className,
}: FeedbackFormProps) {
  const [type, setType] = useState<FeedbackType>("general");
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [stepsToReproduce, setStepsToReproduce] = useState("");
  const [expectedResult, setExpectedResult] = useState("");
  const [actualResult, setActualResult] = useState("");
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [screenshotStorageId, setScreenshotStorageId] = useState<
    Id<"_storage"> | undefined
  >(undefined);
  const [screenshotPreview, setScreenshotPreview] = useState<
    string | undefined
  >(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const submitFeedback = useMutation(api.feedback.create);
  const generateUploadUrl = useMutation(
    api.feedback.generateScreenshotUploadUrl,
  );

  const handleFileSelect = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file (JPG, PNG, or WebP)");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image must be less than 5MB");
      return;
    }

    try {
      setIsUploading(true);

      const reader = new FileReader();
      reader.onload = (e) => setScreenshotPreview(e.target?.result as string);
      reader.readAsDataURL(file);

      const uploadUrl = await generateUploadUrl();

      const result = await fetch(uploadUrl, {
        method: "POST",
        headers: { "Content-Type": file.type },
        body: file,
      });

      const { storageId } = await result.json();
      setScreenshotStorageId(storageId as Id<"_storage">);

      toast.success("Screenshot uploaded");
    } catch (error) {
      console.error("Failed to upload screenshot:", error);
      toast.error("Failed to upload screenshot. Please try again.");
      setScreenshotPreview(undefined);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveScreenshot = () => {
    setScreenshotStorageId(undefined);
    setScreenshotPreview(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const resetForm = () => {
    setType("general");
    setTitle("");
    setMessage("");
    setStepsToReproduce("");
    setExpectedResult("");
    setActualResult("");
    setRating(undefined);
    setScreenshotStorageId(undefined);
    setScreenshotPreview(undefined);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedTitle = title.trim();
    const trimmedMessage = message.trim();

    if (!trimmedTitle || !trimmedMessage) {
      toast.error("Please fill in all required fields");
      return;
    }

    if (trimmedTitle.length < 3) {
      toast.error("Title must be at least 3 characters");
      return;
    }

    if (trimmedMessage.length < 10) {
      toast.error("Description must be at least 10 characters");
      return;
    }

    if (type === "bug") {
      const trimmedSteps = stepsToReproduce.trim();
      const trimmedExpected = expectedResult.trim();
      const trimmedActual = actualResult.trim();

      if (!trimmedSteps || !trimmedExpected || !trimmedActual) {
        toast.error("Please fill in all bug report fields");
        return;
      }

      if (trimmedSteps.length < 10) {
        toast.error("Steps to reproduce must be at least 10 characters");
        return;
      }

      if (trimmedExpected.length < 5 || trimmedActual.length < 5) {
        toast.error(
          "Expected and actual results must be at least 5 characters",
        );
        return;
      }
    }

    try {
      setIsSubmitting(true);

      await submitFeedback({
        type,
        title: trimmedTitle,
        message: trimmedMessage,
        stepsToReproduce: type === "bug" ? stepsToReproduce.trim() : undefined,
        expectedResult: type === "bug" ? expectedResult.trim() : undefined,
        actualResult: type === "bug" ? actualResult.trim() : undefined,
        screenshotStorageId,
        rating,
        url: window.location.href,
        userAgent: navigator.userAgent,
      });

      toast.success("Feedback submitted!", {
        description: "Thank you for helping us improve iTimedIT.",
      });

      resetForm();
      onSubmitted?.();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      const fallbackMessage =
        error instanceof Error ? error.message : "Please try again.";
      const dataMessage =
        typeof (error as { data?: { userMessage?: string } })?.data
          ?.userMessage === "string"
          ? (error as { data?: { userMessage?: string } }).data?.userMessage
          : undefined;
      let parsedMessage: string | undefined;
      if (!dataMessage && error instanceof Error) {
        const raw = error.message.trim();
        const jsonStart = raw.indexOf("{");
        if (jsonStart !== -1) {
          try {
            const parsed = JSON.parse(raw.slice(jsonStart));
            if (typeof parsed?.userMessage === "string") {
              parsedMessage = parsed.userMessage;
            }
          } catch {
            parsedMessage = undefined;
          }
        }
      }
      toast.error("Failed to submit feedback", {
        description: dataMessage || parsedMessage || fallbackMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const feedbackTypes: {
    value: FeedbackType;
    label: string;
    icon: React.ReactNode;
    description: string;
  }[] = [
    {
      value: "bug",
      label: "Bug Report",
      icon: <Bug className="w-4 h-4" />,
      description: "Report something that's not working",
    },
    {
      value: "feature",
      label: "Feature Request",
      icon: <Lightbulb className="w-4 h-4" />,
      description: "Suggest a new feature or improvement",
    },
    {
      value: "general",
      label: "General Feedback",
      icon: <MessageSquare className="w-4 h-4" />,
      description: "Share your thoughts or suggestions",
    },
  ];

  const formClassName = ["space-y-4", className].filter(Boolean).join(" ");

  return (
    <form onSubmit={handleSubmit} className={formClassName}>
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          What would you like to share? *
        </label>
        <div className="grid grid-cols-2 gap-2">
          {feedbackTypes.map((feedbackType) => (
            <button
              key={feedbackType.value}
              type="button"
              onClick={() => setType(feedbackType.value)}
              className={`p-3 rounded-lg border text-left transition-all ${
                type === feedbackType.value
                  ? "border-primary bg-primary/5 dark:bg-primary/10"
                  : "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600"
              }`}
            >
              <div className="flex items-center gap-2 mb-1">
                <span
                  className={
                    type === feedbackType.value
                      ? "text-primary"
                      : "text-gray-500"
                  }
                >
                  {feedbackType.icon}
                </span>
                <span
                  className={`font-medium text-sm ${
                    type === feedbackType.value ? "text-primary" : ""
                  }`}
                >
                  {feedbackType.label}
                </span>
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {feedbackType.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Title *
        </label>
        <Input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Brief summary of your feedback"
          required
          maxLength={200}
        />
      </div>

      {type === "bug" && (
        <div className="space-y-4 p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-900/20">
          <h4 className="font-medium text-red-800 dark:text-red-200 flex items-center gap-2">
            <Bug className="w-4 h-4" />
            Bug Report Details
          </h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Steps to Reproduce *
            </label>
            <textarea
              value={stepsToReproduce}
              onChange={(e) => setStepsToReproduce(e.target.value)}
              placeholder="1. Go to...&#10;2. Click on...&#10;3. Observe..."
              required
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Expected Result *
              </label>
              <textarea
                value={expectedResult}
                onChange={(e) => setExpectedResult(e.target.value)}
                placeholder="What should happen?"
                required
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Actual Result *
              </label>
              <textarea
                value={actualResult}
                onChange={(e) => setActualResult(e.target.value)}
                placeholder="What actually happened?"
                required
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
              />
            </div>
          </div>
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          Description *
        </label>
        <textarea
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder={
            type === "bug"
              ? "Any additional details about the bug..."
              : "Tell us more about your feedback..."
          }
          required
          rows={4}
          maxLength={5000}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
        />
        <p className="text-xs text-gray-500 mt-1">
          {message.length}/5000 characters
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Screenshot (optional)
        </label>
        {screenshotPreview ? (
          <div className="relative inline-block">
            <img
              src={screenshotPreview}
              alt="Screenshot preview"
              className="max-h-40 rounded-lg border border-gray-200 dark:border-gray-700"
            />
            <button
              type="button"
              onClick={handleRemoveScreenshot}
              className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary hover:bg-primary/5 transition-colors disabled:opacity-50"
          >
            {isUploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4" />
                Upload Screenshot
              </>
            )}
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          onChange={handleFileSelect}
          className="hidden"
        />
        <p className="text-xs text-gray-500 mt-1">
          Max 5MB. Supported formats: JPG, PNG, WebP
        </p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          How would you rate your experience? (optional)
        </label>
        <div className="flex items-center gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              onClick={() => setRating(rating === star ? undefined : star)}
              className="p-1 transition-colors"
            >
              <Star
                className={`w-6 h-6 ${
                  rating && star <= rating
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-gray-300 dark:text-gray-600"
                }`}
              />
            </button>
          ))}
        </div>
      </div>

      <div className="flex gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button
          type="submit"
          disabled={isSubmitting || isUploading}
          className="flex-1"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              Submitting...
            </>
          ) : (
            submitLabel
          )}
        </Button>
        {showCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
            disabled={isSubmitting || isUploading}
            className="flex-1"
          >
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
